/**
 * Coach roster invites (Phase 6).
 *
 * - **Send and revoke** are coach calls through the shared api client (token and view header).
 * - **Preview is anonymous**: it bypasses the shared client, like `services/setupInvitations.ts`, so a
 *   signed-in visitor's token never rides along with someone else's invite.
 * - **Accept goes through the shared client**, so a signed-in visitor's token is attached (the API
 *   accepts as that account) and a signed-out one posts with none (the API creates the account).
 *
 * **The invite token travels in the POST body, never the URL.** It is the whole credential: it can
 * create a verified account for the invited address, and a path or query is logged and sent to
 * Sentry.
 */

import { ApiError, del, post } from './api';
import type {
  AcceptTravelBallRosterInviteRequest,
  AcceptTravelBallRosterInviteResponse,
  SendTravelBallRosterInvitesRequest,
  SendTravelBallRosterInvitesResponse,
  TravelBallRosterInvite,
  TravelBallRosterInvitePreview,
} from '../types/travelBallRosterInvites';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const PREVIEW_URL = `${API_BASE_URL}/api/public/travel-ball/roster-invites/preview`;

export const RATE_LIMITED_MESSAGE = 'Too many attempts. Wait a minute and try again.';
export const NETWORK_MESSAGE = 'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';

/**
 * A refused roster invite call. `code` is the server's own (`invite_expired`, `already_claimed`, ...),
 * or `rate_limited`, `network`, `unknown` when the body carried none. `message` is always displayable.
 */
export class RosterInviteError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'RosterInviteError';
    this.code = code;
    this.status = status;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const errorFromBody = (status: number, body: unknown): RosterInviteError => {
  const record = asRecord(body);
  const code = text(record.code);
  const message = text(record.message);
  // A coded 429 (`too_many_invites`) keeps its code; the rate limiter's own 429 has none.
  if (code) return new RosterInviteError(code, message ?? UNKNOWN_MESSAGE, status);
  if (status === 429) return new RosterInviteError('rate_limited', RATE_LIMITED_MESSAGE, status);
  if (status === 401) return new RosterInviteError('unauthorized', message ?? UNKNOWN_MESSAGE, status);
  if (status === 404) return new RosterInviteError('not_found', message ?? UNKNOWN_MESSAGE, status);
  return new RosterInviteError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

/** Any failure from a roster invite call as a `RosterInviteError` (the family Home's in-app accept uses it too). */
export const toRosterInviteError = (error: unknown): RosterInviteError => fromApiError(error);

const fromApiError = (error: unknown): RosterInviteError => {
  if (error instanceof RosterInviteError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) return new RosterInviteError('network', NETWORK_MESSAGE, 0);
    return errorFromBody(error.statusCode, error.response);
  }
  return new RosterInviteError('unknown', UNKNOWN_MESSAGE, 0);
};

const asInvites = (value: unknown): TravelBallRosterInvite[] =>
  Array.isArray(value) ? (value as TravelBallRosterInvite[]) : [];

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/**
 * Invite a roster row's player and/or parents/guardians. An address that already has a pending invite
 * of the same kind answers 409 `already_invited` unless `resend` is set, which replaces it (that is
 * Resend). Missing lists read as empty.
 */
export const sendTravelBallRosterInvites = async (
  rosterRowId: string,
  request: SendTravelBallRosterInvitesRequest,
): Promise<SendTravelBallRosterInvitesResponse> => {
  try {
    const body = await post<SendTravelBallRosterInvitesResponse>(
      `/api/travel-ball/players/${encodeURIComponent(rosterRowId)}/invites`,
      {
        ...(request.playerEmail ? { playerEmail: request.playerEmail } : {}),
        guardianEmails: request.guardianEmails,
        ...(request.resend ? { resend: true } : {}),
      },
    );
    return {
      playerId: body?.playerId ?? rosterRowId,
      pendingInvites: asInvites(body?.pendingInvites),
      unsentEmails: asStrings(body?.unsentEmails),
    };
  } catch (error) {
    throw fromApiError(error);
  }
};

/** Revoke a pending invite. 404 `invite_not_found`, 409 `invite_already_used`. */
export const revokeTravelBallRosterInvite = async (inviteId: string): Promise<void> => {
  try {
    await del<void>(`/api/travel-ball/roster-invites/${encodeURIComponent(inviteId)}`);
  } catch (error) {
    throw fromApiError(error);
  }
};

/** What the invite is for, before anyone types a password. Anonymous. 404 `invite_not_found`. */
export const previewTravelBallRosterInvite = async (
  token: string,
): Promise<TravelBallRosterInvitePreview> => {
  let response: Response;
  try {
    response = await fetch(PREVIEW_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    throw new RosterInviteError('network', NETWORK_MESSAGE, 0);
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw errorFromBody(response.status, body);

  const row = asRecord(body);
  const status = text(row.status);
  if (!status) throw new RosterInviteError('unknown', UNKNOWN_MESSAGE, response.status);
  return {
    status,
    kind: text(row.kind) ?? 'player',
    teamName: text(row.teamName) ?? '',
    organizationName: text(row.organizationName) ?? '',
    playerFirstName: text(row.playerFirstName) ?? '',
    email: text(row.email),
    hasAccount: row.hasAccount === true,
  };
};

/** Accept the invite, signed out (creates the account, 201) or signed in (200). */
export const acceptTravelBallRosterInvite = async (
  request: AcceptTravelBallRosterInviteRequest,
): Promise<AcceptTravelBallRosterInviteResponse> => {
  try {
    return await post<AcceptTravelBallRosterInviteResponse>(
      '/api/public/travel-ball/roster-invites/accept',
      { ...request, platform: 'web' },
    );
  } catch (error) {
    throw fromApiError(error);
  }
};
